import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Swal from 'sweetalert2';
import { getSwalSuccessTheme, getSwalErrorTheme } from '../utils/swalTheme';
import './ContactWidget.css';

function ContactWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setCategory('');
    setSubject('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !subject.trim() || !message.trim()) return;

    setSubmitting(true);
    try {
      await api.post('/contact', { category, subject: subject.trim(), message: message.trim() });
      resetForm();
      setIsOpen(false);
      Swal.fire({
        icon: 'success',
        title: 'Message Sent',
        text: 'Thank you! We\'ll get back to you soon.',
        timer: 3000,
        showConfirmButton: false,
        ...getSwalSuccessTheme()
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Send',
        text: err.message || 'Something went wrong. Please try again.',
        ...getSwalErrorTheme()
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = category && subject.trim() && message.trim() && !submitting;

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          ref={btnRef}
          className="contact-widget-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Contact us"
          title="Contact Us"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Overlay — click to close */}
      {isOpen && <div className="contact-widget-overlay" onClick={() => setIsOpen(false)} />}

      {/* Panel */}
      {isOpen && (
        <div ref={panelRef} className="contact-widget-panel">
          <div className="contact-widget-header">
            <h3>Contact Us</h3>
            <button className="contact-widget-close" onClick={() => setIsOpen(false)} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="contact-widget-body" onSubmit={handleSubmit}>
            {/* User info */}
            <div className="contact-widget-user-info">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span>{user?.name} &middot; {user?.email}</span>
            </div>

            {/* Category */}
            <span className="contact-widget-category-label">Category *</span>
            <div className="contact-widget-categories">
              <button
                type="button"
                className={`contact-widget-category${category === 'technical_issue' ? ' selected' : ''}`}
                onClick={() => setCategory('technical_issue')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Technical Issue
              </button>
              <button
                type="button"
                className={`contact-widget-category${category === 'advertisement' ? ' selected' : ''}`}
                onClick={() => setCategory('advertisement')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
                Advertise with Us
              </button>
            </div>

            {/* Subject */}
            <div className="contact-widget-field">
              <label htmlFor="contact-subject">Subject *</label>
              <input
                id="contact-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description"
                maxLength={255}
                required
              />
            </div>

            {/* Message */}
            <div className="contact-widget-field">
              <label htmlFor="contact-message">Message *</label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us more..."
                maxLength={5000}
                required
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="contact-widget-submit"
              disabled={!canSubmit}
            >
              {submitting ? (
                <>
                  <span className="contact-widget-spinner" />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default ContactWidget;
